import { clamp, requestMedia } from "../shared";

const G = 160, R = 640;
const NAMES = ["C", "C♯", "D", "D♯", "E", "F", "F♯", "G", "G♯", "A", "A♯", "B"];
export const noteName = (f) => { const n = Math.round(12 * Math.log2(f / 440) + 69); return NAMES[((n % 12) + 12) % 12] + (Math.floor(n / 12) - 1); };
export const MEDIA = {
  sand: { label: "Desert sand", rgb: [255, 190, 105] },
  salt: { label: "Sea salt", rgb: [225, 240, 255] },
  iron: { label: "Iron filings", rgb: [150, 175, 215] },
  quartz: { label: "Quartz glow", rgb: [90, 245, 255] },
};
export const PLATES = { steel: { label: "Steel", k: 1, tint: [0.85, 1, 1.35] }, brass: { label: "Brass", k: 0.72, tint: [1.5, 1.15, 0.6] }, aluminium: { label: "Aluminium", k: 1.25, tint: [1.1, 1.15, 1.25] }, glass: { label: "Glass", k: 1.45, tint: [0.7, 1.25, 1.45] } };
export const STUDIO_DEFAULTS = { shape: "square", plate: "steel", plateSize: 0.9, medium: "sand", grains: 30000, grainSize: 1, kick: 1.2, friction: 0.2, settle: 1, damping: 0.25, intensity: 1, simSpeed: 1, Q: 20, stiffness: 1, viz: "sand", view: "top", overlay: false, glow: true, volume: 0.25, micGain: 1.5, wave: "sine", hold: false, freq: 432, tone: false };

function besselJ(m, x) {
  let f = 1; for (let i = 2; i <= m; i++) f *= i;
  let term = Math.pow(x / 2, m) / f, sum = 0;
  for (let k = 0; k < 60; k++) { sum += term; term *= -(x * x) / 4 / ((k + 1) * (k + 1 + m)); if (Math.abs(term) < 1e-9) break; }
  return sum;
}
const SQUARE = [], CIRCLE = [];
for (let n = 0; n <= 16; n += 2) for (let m = n; m <= 16; m += 2) if (n + m > 0) SQUARE.push({ n, m, k: n * n + m * m, label: `${n},${m}` });
for (let m = 0; m <= 8; m++) for (let n = 1; n <= 5; n++) { const b = (n + m / 2 - 0.25) * Math.PI, j = b - (4 * m * m - 1) / (8 * b); CIRCLE.push({ m, n, j, k: j * j, label: `${m}·${n}` }); }

export class SandStudio {
  constructor(canvas, spectrum, trace, { onTelemetry, onParam }) {
    this.cv = canvas; this.g = canvas.getContext("2d"); this.spec = spectrum; this.trace = trace; this.onTelemetry = onTelemetry; this.onParam = onParam;
    this.p = { ...STUDIO_DEFAULTS }; this.f = this.p.freq; this.drive = 0; this.level = 0; this.source = "Idle"; this.cache = new Map();
    this.U = new Float32Array(G * G); this.lastKey = ""; this.t = 0; this.burst = 0; this.transients = 0; this.avgRms = 0; this.rms = 0; this.hist = []; this.log = [];
    this.off = document.createElement("canvas"); this.off.width = this.off.height = R; this.og = this.off.getContext("2d");
    this.img = this.og.createImageData(R, R); this.base = new Uint8ClampedArray(R * R * 4);
    this.alloc(); this.buildBase();
    this.resize(); this.ro = new ResizeObserver(() => this.resize()); this.ro.observe(canvas.parentElement);
    this.last = performance.now(); this.telT = 0; this.loop = this.loop.bind(this); this.raf = requestAnimationFrame(this.loop);
  }

  // ---------- state ----------
  set(key, val) {
    this.p[key] = val;
    if (key === "grains") this.alloc();
    if (key === "shape") { this.cache.clear(); this.lastKey = ""; this.scatter(); }
    if (key === "stiffness" || key === "Q" || key === "plate" || key === "damping") this.lastKey = "";
    if (key === "plate") this.dirtyBase = true;
    if (key === "plateSize") this.resize();
    if (key === "overlay" || key === "shape") this.dirtyBase = true;
    if (key === "freq") { this.f = val; this.sweep = null; if (this.toneOn) this.osc.frequency.setTargetAtTime(val, this.ac.currentTime, 0.02); }
    if (key === "tone") val ? this.noteOn(this.p.freq, true) : this.noteOff(true);
    if (key === "wave" && this.osc) this.osc.type = val;
    if (key === "volume" && this.toneOn) this.gain.gain.setTargetAtTime(Math.min(0.5, val) * 0.3, this.ac.currentTime, 0.02);
  }
  alloc() { this.n = this.p.grains; this.pos = new Float32Array(this.n * 2); this.scatter(); }
  scatter() {
    for (let i = 0; i < this.n; i++) {
      let x, y;
      do { x = Math.random(); y = Math.random(); } while (this.p.shape === "circle" && (x - 0.5) ** 2 + (y - 0.5) ** 2 > 0.235);
      this.pos[i * 2] = x; this.pos[i * 2 + 1] = y;
    }
  }

  // ---------- plate physics ----------
  modes() { return this.p.shape === "circle" ? CIRCLE : SQUARE; }
  baseF() { return (this.p.shape === "circle" ? 4.6 : 14) * this.p.stiffness * PLATES[this.p.plate].k; }
  modeField(md) {
    const key = `${this.p.shape}:${md.label}`; if (this.cache.has(key)) return this.cache.get(key);
    const a = new Float32Array(G * G);
    for (let r = 0; r < G; r++) for (let c = 0; c < G; c++) {
      const x = (c + 0.5) / G, y = (r + 0.5) / G;
      if (this.p.shape === "circle") {
        const dx = x - 0.5, dy = y - 0.5, rr = Math.hypot(dx, dy) * 2;
        a[r * G + c] = rr > 1 ? 0 : besselJ(md.m, md.j * rr) * Math.cos(md.m * Math.atan2(dy, dx)) * 2.2;
      } else {
        // centre-driven free square plate (Chladni/Ritz approx.): u = cos(nπx)cos(mπy) + cos(mπx)cos(nπy), n,m even → antinode at the drive point
        const pn = md.n * Math.PI, pm = md.m * Math.PI;
        a[r * G + c] = (Math.cos(pn * x) * Math.cos(pm * y) + Math.cos(pm * x) * Math.cos(pn * y)) * 0.5;
      }
    }
    this.cache.set(key, a); return a;
  }
  resonance() {
    const b = this.baseF(), Q = this.p.Q * (1 - this.p.damping * 0.85);
    return this.modes().map((md) => { const f0 = b * md.k, x = (this.f - f0) / (f0 / Q); return { md, f0, w: 1 / (1 + x * x) }; }).sort((a, c) => c.w - a.w).slice(0, 2);
  }
  shape() {
    const key = `${this.p.shape}:${Math.round(this.f * 2)}:${this.p.Q}:${this.p.stiffness}:${this.p.plate}:${this.p.damping}`;
    if (key === this.lastKey) return; this.lastKey = key;
    this.res = this.resonance(); const U = this.U; U.fill(0);
    if (this.res[1] && this.res[1].w < this.res[0].w * 0.6) this.res = [this.res[0]];
    for (const { md, w } of this.res) { const a = this.modeField(md); for (let i = 0; i < U.length; i++) U[i] += w * a[i]; }
    if (this.p.overlay) this.dirtyBase = true;
  }
  grains(dt) {
    // response = drive x (floor + resonance): any sound shakes the plate, resonance makes it decisive
    const s = this.drive * this.p.intensity * (0.3 + 0.7 * (this.res?.[0].w ?? 0)), burst = this.burst * 0.012;
    const kk = this.p.kick * (1 - this.p.friction) * s * dt * 0.5, drift = 0.9 * this.p.settle, U = this.U, P = this.pos, circ = this.p.shape === "circle";
    const st = this.stir;
    let onNodes = 0;
    for (let i = 0; i < this.n; i++) {
      let x = P[i * 2], y = P[i * 2 + 1];
      const c = clamp((x * G) | 0, 2, G - 3), r = clamp((y * G) | 0, 2, G - 3), id = r * G + c, a = Math.min(1, Math.abs(U[id]) * 1.4);
      if (a < 0.1) onNodes++;
      if (kk > 0 && a > 0.004) {
        // grains bounce randomly in proportion to local vibration and are biased down the amplitude gradient toward nodal lines
        const gx = Math.abs(U[id + 2]) - Math.abs(U[id - 2]), gy = Math.abs(U[id + 2 * G]) - Math.abs(U[id - 2 * G]), gl = Math.hypot(gx, gy) + 1e-6;
        const hop = Math.pow(a, 0.8) * kk;
        x += hop * ((Math.random() - 0.5) * 1.6 - (gx / gl) * drift); y += hop * ((Math.random() - 0.5) * 1.6 - (gy / gl) * drift);
      }
      if (kk > 0) { const j = kk * 0.09; x += (Math.random() - 0.5) * j; y += (Math.random() - 0.5) * j; }
      if (burst > 0) { x += (Math.random() - 0.5) * burst; y += (Math.random() - 0.5) * burst; }
      if (st) { const dx = x - st.x, dy = y - st.y, d2 = dx * dx + dy * dy; if (d2 < 0.0036) { const d = Math.sqrt(d2) + 1e-4, push = (0.06 - d) * 0.5; x += (dx / d) * push; y += (dy / d) * push; } }
      if (circ) { const dx = x - 0.5, dy = y - 0.5, d = Math.hypot(dx, dy); if (d > 0.485) { x = 0.5 + (dx / d) * 0.485; y = 0.5 + (dy / d) * 0.485; } }
      else { x = clamp(x, 0.004, 0.996); y = clamp(y, 0.004, 0.996); }
      P[i * 2] = x; P[i * 2 + 1] = y;
    }
    this.onNodes = onNodes / this.n;
  }

  // ---------- rendering ----------
  buildBase() {
    const b = this.base, circ = this.p.shape === "circle", U = this.U, ov = this.p.overlay;
    for (let y = 0; y < R; y++) for (let x = 0; x < R; x++) {
      const i = (y * R + x) * 4, nx = x / R, ny = y / R, dx = nx - 0.5, dy = ny - 0.5, rr = Math.hypot(dx, dy);
      if (circ && rr > 0.495) { b[i + 3] = 0; continue; }
      const edge = circ ? 0.495 - rr : Math.min(nx, ny, 1 - nx, 1 - ny);
      const brushed = Math.sin(ny * 900 + Math.sin(nx * 40) * 2) * 2 + Math.sin(rr * 2400) * 1.2;
      let v = 18 + brushed - rr * 14 + (edge < 0.006 ? 40 : 0);
      const grid = (x % 40 === 0 || y % 40 === 0) ? 6 : 0;
      const tn = PLATES[this.p.plate].tint; let cr = v * tn[0], cg = v * tn[1] + grid, cb = v * tn[2] + grid * 1.5;
      if (rr < 0.012) { cr = cg = cb = 90 - rr * 3000; }
      if (ov) { const u = U[((ny * G) | 0) * G + ((nx * G) | 0)], a = Math.min(1, Math.abs(u)) * 70; if (u > 0) { cg += a; cb += a * 1.1; } else { cr += a * 1.2; cb += a * 0.8; } }
      b[i] = cr; b[i + 1] = cg; b[i + 2] = cb; b[i + 3] = 255;
    }
    this.dirtyBase = false;
  }
  render() {
    if (this.dirtyBase) this.buildBase();
    const d = this.img.data, P = this.pos, [cr, cg, cb] = MEDIA[this.p.medium].rgb, k = this.n > 40000 ? 0.38 : 0.55;
    d.set(this.base);
    const field = this.p.viz === "wave" || this.p.viz === "amplitude", sa = field ? 0.3 : 1, gs = this.p.grainSize | 0;
    for (let i = 0; i < this.n; i++) {
      const px = (P[i * 2] * R) | 0, py = (P[i * 2 + 1] * R) | 0;
      for (let oy = 0; oy < gs; oy++) for (let ox = 0; ox < gs; ox++) {
        const j = (Math.min(R - 1, py + oy) * R + Math.min(R - 1, px + ox)) * 4;
        d[j] += cr * k * sa; d[j + 1] += cg * k * sa; d[j + 2] += cb * k * sa;
      }
    }
    if (this.p.viz === "nodal" || field) this.paintField(d);
    this.og.putImageData(this.img, 0, 0);
    const g = this.g, { w, h } = this, S = this.S, ox = (w - S) / 2, oy = (h - S) / 2;
    g.clearRect(0, 0, w, h);
    g.drawImage(this.off, ox, oy, S, S);
    if (this.p.glow) {
      if (!this.glowCv) { this.glowCv = document.createElement("canvas"); this.glowCv.width = this.glowCv.height = 128; this.gg = this.glowCv.getContext("2d"); }
      this.gg.clearRect(0, 0, 128, 128); this.gg.drawImage(this.off, 0, 0, 128, 128);
      g.save(); g.globalCompositeOperation = "lighter"; g.globalAlpha = 0.35; g.imageSmoothingQuality = "high"; g.drawImage(this.glowCv, ox, oy, S, S); g.restore();
    }
    this.plateRect = { ox, oy, S };
  }
  paintField(d) {
    const U = this.U, mode = this.p.viz, ph = Math.sin(this.t * 18), circ = this.p.shape === "circle", sc = R / G;
    for (let r = 0; r < G; r++) for (let c = 0; c < G; c++) {
      const u = U[r * G + c], a = Math.abs(u);
      let cr = 0, cg = 0, cb = 0;
      if (mode === "nodal") { const l = Math.max(0, 1 - a / 0.05); cr = 255 * l * 0.2; cg = 240 * l; cb = 255 * l; }
      else if (mode === "wave") { const w = u * ph * 2.2; if (w > 0) { cg = 240 * w; cb = 255 * w; } else { cr = 255 * -w; cb = 154 * -w; } }
      else { const t = Math.min(1, a * 1.6); cr = 255 * t; cg = 120 * t * t + 30 * t; cb = 255 * (1 - t) * t * 2.5; }
      for (let y = Math.floor(r * sc); y < Math.floor((r + 1) * sc); y++) for (let x = Math.floor(c * sc); x < Math.floor((c + 1) * sc); x++) {
        if (circ && (x / R - 0.5) ** 2 + (y / R - 0.5) ** 2 > 0.245) continue;
        const j = (y * R + x) * 4; d[j] += cr * 0.8; d[j + 1] += cg * 0.8; d[j + 2] += cb * 0.8;
      }
    }
  }
  drawTrace() {
    const c = this.trace; if (!c) return;
    const g = c.getContext("2d"), W = c.width, H = c.height, h = this.hist; g.clearRect(0, 0, W, H);
    g.strokeStyle = "rgba(0,240,255,0.15)"; [100, 300, 1000].forEach((f) => { const y = H - (Math.log(f / 50) / Math.log(60)) * H; g.beginPath(); g.moveTo(0, y); g.lineTo(W, y); g.stroke(); });
    g.strokeStyle = "#ffb800"; g.lineWidth = 1.5; g.beginPath();
    h.forEach((f, i) => { const x = (i / 239) * W, y = H - (Math.log(Math.max(50, f) / 50) / Math.log(60)) * H; f ? (i && h[i - 1] ? g.lineTo(x, y) : g.moveTo(x, y)) : null; }); g.stroke();
  }
  analyse(dt) {
    const an = this.an; if (!an) return;
    an.getFloatTimeDomainData(this.td);
    const b = this.td, n = b.length, sr = this.ac.sampleRate; let rms = 0; for (let i = 0; i < n; i++) rms += b[i] * b[i]; rms = Math.sqrt(rms / n);
    this.rms = rms; this.avgRms = this.avgRms * 0.95 + rms * 0.05;
    if (rms > 0.04 && rms > this.avgRms * 2.6) { this.burst = 1; this.transients++; }
    this.burst = Math.max(0, this.burst - dt * 1.8);
    this.f0 = 0; this.conf = 0;
    if (rms > 0.008) {
      let best = 0, lag = 0; const r0 = rms * rms * n;
      for (let L = Math.floor(sr / 1500); L < Math.floor(sr / 60); L++) { let s = 0; for (let i = 0; i < n - L; i += 2) s += b[i] * b[i + L]; s *= 2; if (s > best) { best = s; lag = L; } }
      this.conf = best / r0; if (this.conf > 0.55 && lag) this.f0 = sr / lag;
    }
    const hz = sr / an.fftSize; let peak = 0, bi = 0;
    for (let i = Math.ceil(60 / hz); i < 3000 / hz; i++) if (this.bins[i] > peak) { peak = this.bins[i]; bi = i; }
    this.dominant = peak > 40 ? bi * hz : 0;
    const base = this.f0 || this.dominant;
    this.harm = [1, 2, 3, 4, 5].map((k) => (base ? this.bins[Math.min(this.bins.length - 1, Math.round((base * k) / hz))] / 255 : 0));
  }
  drawSpectrum() {
    const c = this.spec; if (!c) return;
    const g = c.getContext("2d"), W = c.width, H = c.height; g.clearRect(0, 0, W, H);
    if (!this.an) { g.fillStyle = "rgba(0,240,255,0.25)"; g.fillRect(0, H - 1, W, 1); return; }
    const bins = this.bins, hz = this.ac.sampleRate / this.an.fftSize, maxBin = Math.min(bins.length, 3200 / hz);
    const grd = g.createLinearGradient(0, H, 0, 0); grd.addColorStop(0, "#9b5cff"); grd.addColorStop(1, "#00f0ff"); g.fillStyle = grd;
    for (let x = 0; x < W; x += 2) {
      const f = 40 * Math.pow(3200 / 40, x / W), v = bins[Math.min(maxBin | 0, (f / hz) | 0)] / 255;
      g.fillRect(x, H - v * H, 1.5, v * H);
    }
    const fx = (Math.log(this.f / 40) / Math.log(80)) * W; g.fillStyle = "#ffb800"; g.fillRect(fx, 0, 1.5, H);
  }
  resize() {
    const el = this.cv.parentElement, dpr = Math.min(window.devicePixelRatio || 1, 2);
    this.w = el.clientWidth; this.h = el.clientHeight; if (!this.w || !this.h) return;
    this.cv.width = this.w * dpr; this.cv.height = this.h * dpr; this.cv.style.width = `${this.w}px`; this.cv.style.height = `${this.h}px`;
    this.g.setTransform(dpr, 0, 0, dpr, 0, 0); this.S = Math.min(this.w, this.h) * this.p.plateSize;
  }

  // ---------- audio ----------
  ensureAudio() {
    if (this.ac) return;
    const AC = window.AudioContext || window.webkitAudioContext; this.ac = new AC();
    this.an = this.ac.createAnalyser(); this.an.fftSize = 8192; this.an.smoothingTimeConstant = 0.6; this.bins = new Uint8Array(this.an.frequencyBinCount);
    this.td = new Float32Array(2048); this.recDest = this.ac.createMediaStreamDestination();
  }
  noteOn(f, sustained = false) {
    this.ensureAudio(); if (this.ac.state !== "running") this.ac.resume?.(); this.stopInputs();
    if (!this.osc) {
      this.osc = this.ac.createOscillator(); this.gain = this.ac.createGain(); this.gain.gain.value = 0;
      this.osc.connect(this.gain); this.gain.connect(this.an); this.gain.connect(this.recDest); this.gain.connect(this.ac.destination); this.osc.start();
    }
    this.osc.type = this.p.wave; this.osc.frequency.setTargetAtTime(f, this.ac.currentTime, 0.01);
    this.gain.gain.setTargetAtTime(Math.min(0.5, this.p.volume) * 0.3, this.ac.currentTime, 0.02);
    this.f = f; this.sweep = null; this.toneOn = true; this.sustained = sustained; this.source = sustained ? "Sustained tone" : "Keyboard";
    this.onParam?.("freq", clamp(Math.round(f), 60, 3000));
    if (sustained !== !!this.p.tone) { this.p.tone = sustained; this.onParam?.("tone", sustained); }
  }
  noteOff(force = false) {
    if (!this.toneOn || (!force && (this.p.hold || this.sustained))) return;
    this.gain.gain.setTargetAtTime(0, this.ac.currentTime, 0.12); this.toneOn = false; this.source = "Idle";
    if (force && this.p.tone) this.onParam?.("tone", false);
  }
  stopInputs() {
    this.stream?.getTracks().forEach((t) => t.stop()); this.stream = null; this.micSrc?.disconnect(); this.micSrc = null;
    if (this.audioEl) { this.audioEl.pause(); this.fileSrc?.disconnect(); this.audioEl = null; this.fileSrc = null; }
  }
  async startMic() {
    this.ensureAudio(); this.noteOff(true); this.stopInputs(); this.source = "Requesting mic…";
    await this.ac.resume?.();
    try { this.stream = await requestMedia({ audio: { echoCancellation: false, noiseSuppression: false, autoGainControl: false } }); } catch (e) { this.source = "Idle"; throw e; }
    await this.ac.resume?.();
    this.micSrc = this.ac.createMediaStreamSource(this.stream); this.micSrc.connect(this.an); this.micSrc.connect(this.recDest); this.source = "Microphone";
    return "Listening — sing, whistle or play near the mic";
  }
  async playFile(file) {
    this.ensureAudio(); this.noteOff(true); this.stopInputs();
    const el = new Audio(URL.createObjectURL(file)); el.loop = true; el.crossOrigin = "anonymous";
    this.fileSrc = this.ac.createMediaElementSource(el); this.fileSrc.connect(this.an); this.fileSrc.connect(this.recDest); this.fileSrc.connect(this.ac.destination);
    await this.ac.resume?.(); await el.play(); this.audioEl = el; this.source = `File: ${file.name.slice(0, 18)}`;
    return `Playing ${file.name} into the plate`;
  }
  stopAll() { if (this.ac) { this.noteOff(true); this.stopInputs(); } this.sweep = null; this.source = "Idle"; }
  startSweep() { this.noteOn(80, true); this.sweep = { t: 0 }; this.source = "Sweep"; }
  jump(dir) {
    const b = this.baseF(), fs = this.modes().map((m) => b * m.k).filter((f) => f >= 60 && f <= 3000).sort((a, c) => a - c);
    const next = dir > 0 ? fs.find((f) => f > this.f + 1) : [...fs].reverse().find((f) => f < this.f - 1);
    if (next) this.noteOn(next, true);
  }
  listen(dt) {
    if (!this.an) return;
    this.an.getByteFrequencyData(this.bins);
    if (this.sweep) { this.sweep.t += dt; const f = 80 * Math.pow(30, this.sweep.t / 30); if (f > 2400) this.sweep = null; else { this.f = f; this.osc.frequency.setTargetAtTime(f, this.ac.currentTime, 0.02); } }
    if (this.toneOn) { this.drive = Math.min(1, this.drive + dt * 6); this.level = this.p.volume; return; }
    if (this.micSrc || this.fileSrc) {
      const target = this.f0 || this.dominant;
      const rms = this.rms * (this.p.micGain ?? 1.5);
      this.level = clamp(rms * 6, 0, 1);
      if (target >= 60 && target <= 3000) this.f += (target - this.f) * 0.35;
      this.drive = clamp((rms - 0.004) * 22, 0, 1);
      return;
    }
    this.drive = Math.max(0, this.drive - dt * 1.5); this.level = 0;
  }

  // ---------- loop ----------
  setStir(pt) {
    if (!pt) { this.stir = null; return; }
    const { ox, oy, S } = this.plateRect || {}; if (!S) return;
    const r = this.cv.getBoundingClientRect();
    this.stir = { x: (pt.x - r.left - ox) / S, y: (pt.y - r.top - oy) / S };
  }
  loop(now) {
    this.raf = requestAnimationFrame(this.loop);
    const dt = Math.max(0, Math.min(0.05, (now - this.last) / 1000)); this.last = now;
    const sdt = dt * this.p.simSpeed; this.t += sdt;
    this.analyse(dt); this.listen(dt); this.shape(); this.grains(sdt); this.render(); this.drawSpectrum(); this.drawTrace();
    if (now - this.telT > 250) {
      this.telT = now; const r = this.res?.[0], active = this.source !== "Idle";
      this.hist.push(active ? this.f : 0); if (this.hist.length > 240) this.hist.shift();
      if (active) { this.log.push({ t: +(this.t.toFixed(2)), source: this.source, measured_fundamental_hz: +(this.f0 || 0).toFixed(1), measured_dominant_hz: +(this.dominant || 0).toFixed(1), rms_db: this.rms ? +(20 * Math.log10(this.rms)).toFixed(1) : -120, driven_hz: +this.f.toFixed(1), simulated_mode: r?.md.label, resonance: +(r?.w || 0).toFixed(3) }); if (this.log.length > 20000) this.log.shift(); }
      this.onTelemetry?.({ f: this.f, note: noteName(this.f), mode: r?.md.label ?? "—", res: r ? r.w : 0, modes: (this.res || []).map((m) => ({ label: m.md.label, f0: m.f0, w: m.w })), source: this.source, drive: this.drive, level: this.level, onNodes: this.onNodes || 0, grains: this.n,
        f0: this.f0, conf: this.conf, dominant: this.dominant, rmsDb: this.rms ? 20 * Math.log10(this.rms) : -120, harm: this.harm || [], transients: this.transients, burst: this.burst, baseF: this.baseF(), shape: this.p.shape, recording: !!this.rec });
    }
  }
  startRec() {
    this.ensureAudio(); this.chunks = [];
    this.rec = new MediaRecorder(this.recDest.stream); this.rec.ondataavailable = (e) => e.data.size && this.chunks.push(e.data);
    this.rec.onstop = () => { const a = document.createElement("a"); a.href = URL.createObjectURL(new Blob(this.chunks, { type: "audio/webm" })); a.download = `cymatics-audio-${Date.now()}.webm`; a.click(); this.rec = null; };
    this.rec.start();
  }
  stopRec() { this.rec?.stop(); }
  exportLog() {
    if (!this.log.length) return false;
    const keys = Object.keys(this.log[0]), csv = [keys.join(","), ...this.log.map((r) => keys.map((k) => r[k]).join(","))].join("\n");
    const a = document.createElement("a"); a.href = URL.createObjectURL(new Blob([csv], { type: "text/csv" })); a.download = `cymatics-frequency-log-${Date.now()}.csv`; a.click(); return true;
  }
  capture() { const r = this.res?.[0]; return { img: this.cv.toDataURL("image/png"), f: this.f, note: noteName(this.f), mode: r?.md.label ?? "—", shape: this.p.shape, at: new Date() }; }
  exportPNG() {
    const a = document.createElement("a"); a.href = this.cv.toDataURL("image/png"); a.download = `cymatics-${Math.round(this.f)}hz-${Date.now()}.png`; a.click();
  }
  dispose() { cancelAnimationFrame(this.raf); this.ro.disconnect(); this.stopInputs(); this.ac?.close?.(); }
}
